import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retryWhen, mergeMap, delay, take, finalize } from 'rxjs/operators';
import { FeedbackService } from './feedback.service';

export interface RetryConfig {
  maxRetries: number;
  delayMs: number;
  retryableStatuses: number[];
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 2,
  delayMs: 1000,
  retryableStatuses: [403, 429, 500, 502, 503, 504]
};

@Injectable({
  providedIn: 'root'
})
export class HttpWrapperService {
  constructor(
    private http: HttpClient,
    private feedbackService: FeedbackService
  ) {}

  // GET request with retries
  get<T>(url: string, options: {
    params?: HttpParams;
    headers?: any;
    retryConfig?: Partial<RetryConfig>;
    showErrors?: boolean;
  } = {}): Observable<T> {
    const config = { ...DEFAULT_RETRY_CONFIG, ...(options.retryConfig || {}) };
    const showErrors = options.showErrors !== false;

    return this.http.get<T>(url, {
      params: options.params,
      headers: options.headers
    }).pipe(
      this.configureRetry(config),
      catchError(error => {
        if (showErrors) {
          this.handleError(error, `GET request to ${url} failed`);
        }
        return throwError(() => error);
      })
    );
  }

  // POST request with retries
  post<T>(url: string, body: any, options: {
    params?: HttpParams;
    headers?: any;
    retryConfig?: Partial<RetryConfig>;
    showErrors?: boolean;
  } = {}): Observable<T> {
    const config = { ...DEFAULT_RETRY_CONFIG, ...(options.retryConfig || {}) };
    const showErrors = options.showErrors !== false;

    return this.http.post<T>(url, body, {
      params: options.params,
      headers: options.headers
    }).pipe(
      this.configureRetry(config),
      catchError(error => {
        if (showErrors) {
          this.handleError(error, `POST request to ${url} failed`);
        }
        return throwError(() => error);
      })
    );
  }

  // PUT request with retries
  put<T>(url: string, body: any, options: {
    params?: HttpParams;
    headers?: any;
    retryConfig?: Partial<RetryConfig>;
    showErrors?: boolean;
  } = {}): Observable<T> {
    const config = { ...DEFAULT_RETRY_CONFIG, ...(options.retryConfig || {}) };
    const showErrors = options.showErrors !== false;

    return this.http.put<T>(url, body, {
      params: options.params,
      headers: options.headers
    }).pipe(
      this.configureRetry(config),
      catchError(error => {
        if (showErrors) {
          this.handleError(error, `PUT request to ${url} failed`);
        }
        return throwError(() => error);
      })
    );
  }

  // DELETE request with retries
  delete<T>(url: string, options: {
    params?: HttpParams;
    headers?: any;
    retryConfig?: Partial<RetryConfig>;
    showErrors?: boolean;
  } = {}): Observable<T> {
    const config = { ...DEFAULT_RETRY_CONFIG, ...(options.retryConfig || {}) };
    const showErrors = options.showErrors !== false;

    return this.http.delete<T>(url, {
      params: options.params,
      headers: options.headers
    }).pipe(
      this.configureRetry(config),
      catchError(error => {
        if (showErrors) {
          this.handleError(error, `DELETE request to ${url} failed`);
        }
        return throwError(() => error);
      })
    );
  }

  private configureRetry(config: RetryConfig) {
    return retryWhen<any>(errors => 
      errors.pipe(
        mergeMap((error, i) => {
          const retryAttempt = i + 1;
          
          // Don't retry CORS errors as they will always fail
          if (this.isCorsError(error)) {
            return throwError(() => error);
          }
          
          // Only retry if the error status is in the retryable statuses and we haven't reached max retries
          if (
            retryAttempt <= config.maxRetries && 
            error instanceof HttpErrorResponse && 
            config.retryableStatuses.includes(error.status)
          ) {
            console.log(`Attempt ${retryAttempt}: Retrying request after ${config.delayMs}ms due to ${error.status} error`);
            return throwError(error).pipe(delay(config.delayMs));
          }
          
          return throwError(() => error);
        })
      )
    );
  }
  
  /**
   * Detects if an error is related to CORS issues
   */
  private isCorsError(error: any): boolean {
    // CORS errors often have these characteristics
    return (
      error instanceof HttpErrorResponse && 
      (error.status === 0 || error.status === undefined) &&
      (
        error.message.includes('CORS') || 
        error.message.includes('Cross-Origin Request Blocked') ||
        error.message.includes('NetworkError') ||
        error.message.includes('has been blocked by CORS policy')
      )
    );
  }

  private handleError(error: any, message: string): void {
    console.error(message, error);
    
    if (error instanceof HttpErrorResponse) {
      if (this.isCorsError(error)) {
        this.feedbackService.emitError('Network error: Unable to connect to the server due to CORS policy. Please contact support.');
      } else if (error.status === 403) {
        this.feedbackService.emitError('Your session has expired. Please log in again.');
      } else {
        this.feedbackService.emitError(`${message}: ${error.status} ${error.statusText}`);
      }
    } else {
      this.feedbackService.emitError(`${message}: ${error.message}`);
    }
  }
}